// client/src/components/moonbeams/ViewRecipientsPanel.js
import { useState, useEffect } from 'react';
import moonbeamService from '../../services/moonbeamService.js';
import RescheduleModal from './RescheduleModal.js';

const RECIPIENTS_PER_PAGE = 30;

// ─── Helper: Format birthday as "Mar 22" ───────────────────
function formatBirthday(month, day) {
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[(month || 1) - 1]} ${day || 1}`;
}

// ─── Helper: Format time as "9:00 AM" ──────────────────────
function formatTime(timeStr) {
  if (!timeStr) return '9:00 AM';
  const [hours, minutes] = timeStr.split(':').map(Number);
  const h = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours < 12 ? 'AM' : 'PM';
  return `${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
}

// ─── Helper: Format scheduled date/time ────────────────────
function formatScheduledDateTime(dateStr) {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const h = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
  const ampm = hours < 12 ? 'AM' : 'PM';
  return `${month} ${day}, ${h}:${String(minutes).padStart(2, '0')} ${ampm}`;
}

// ─── Recipient Row Component ───────────────────────────────
function RecipientRow({ recipient, moonbeamUuid, onReschedule, onCancel }) {
  const [copiedUrl, setCopiedUrl] = useState(false);

  const name = [recipient.firstname, recipient.lastname].filter(Boolean).join(' ') 
    || recipient.recipientname 
    || recipient.email;
  
  const viewed = recipient.viewcount > 0;
  const sent = !!recipient.sentat;
  const isBirthday = recipient.deliverymode === 'birthday';
  const isScheduled = !sent && !!recipient.scheduledsendat;
  const isPendingBirthday = !sent && isBirthday;
  const isPending = !sent; // Can reschedule/cancel if not yet sent

  // Build personalized card URL
  const getCardUrl = () => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/moonbeam/${moonbeamUuid}${recipient.viewtoken ? `?r=${recipient.viewtoken}` : ''}`;
  };

  const handleView = () => {
    window.open(getCardUrl(), '_blank', 'noopener,noreferrer');
  };

  const handleCopyUrl = async () => {
    const url = getCardUrl();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch {
      // Fallback
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    }
  };

  // Detect if we're on a mobile device (more reliable than checking navigator.share)
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    typeof navigator !== 'undefined' ? navigator.userAgent : ''
  );

  const handleShare = async () => {
    const url = getCardUrl();
    // Only attempt native share on mobile where it actually works well
    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title: 'You have an eCard!',
          text: `An eCard is waiting for ${name}`,
          url,
        });
        return; // Success - don't fall through
      } catch (err) {
        // User cancelled - don't do anything
        if (err.name === 'AbortError') return;
        // Other error - fall through to copy
      }
    }
    // Fallback to copy
    handleCopyUrl();
  };

  // ─── Status badge ────────────────────────────────────────
  const renderStatusBadge = () => {
    if (viewed) {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-400 font-medium">
          Viewed
        </span>
      );
    }
    if (isPendingBirthday) {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-purple-500/20 text-purple-400 font-medium">
          🎂 {formatBirthday(recipient.birthmonth, recipient.birthdate)}
        </span>
      );
    }
    if (isScheduled) {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-blue-500/20 text-blue-400 font-medium">
          ⏱️ {formatScheduledDateTime(recipient.scheduledsendat)}
        </span>
      );
    }
    if (sent && !viewed) {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-amber-500/20 text-amber-400 font-medium">
          Not opened
        </span>
      );
    }
    return (
      <span className="text-xs px-2 py-1 rounded-full bg-gray-500/20 text-gray-400 font-medium">
        Pending
      </span>
    );
  };

  // ─── Status dot color ────────────────────────────────────
  const getStatusDotClass = () => {
    if (viewed) return 'bg-emerald-400';
    if (isPendingBirthday) return 'bg-purple-400';
    if (isScheduled) return 'bg-blue-400';
    if (sent) return 'bg-amber-400';
    return 'bg-gray-600';
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 px-4 border-b border-white/[0.04] last:border-0 hover:bg-white/[0.02] transition-colors">
      {/* Left: Name and email */}
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <span className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${getStatusDotClass()}`} />
        <div className="min-w-0 flex-1">
          <p className="text-white text-sm font-medium truncate">{name}</p>
          <p className="text-gray-500 text-xs truncate">{recipient.email}</p>
        </div>
      </div>

      {/* Middle: Status badge */}
      <div className="flex-shrink-0 sm:w-40 flex sm:justify-center">
        {renderStatusBadge()}
      </div>

      {/* Right: Action buttons */}
      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
        <button
          onClick={handleView}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-royal-700/50 hover:bg-royal-700 text-royal-100 hover:text-white text-xs transition-colors"
        >
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span className="hidden sm:inline">View</span>
        </button>

        <button
          onClick={handleCopyUrl}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-royal-700/50 hover:bg-royal-700 text-royal-100 hover:text-white text-xs transition-colors"
        >
          {copiedUrl ? (
            <>
              <svg className="w-3.5 h-3.5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-emerald-400 hidden sm:inline">Copied!</span>
            </>
          ) : (
            <>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              <span className="hidden sm:inline">Copy</span>
            </>
          )}
        </button>

        {/* Share button - only show on mobile */}
        {isMobile && (
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-gold/20 hover:bg-gold/30 text-gold text-xs transition-colors"
            title="Share via SMS, WhatsApp, etc."
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
            </svg>
            <span className="hidden sm:inline">Share</span>
          </button>
        )}

        {/* Reschedule button - only for pending (not yet sent) */}
        {isPending && (
          <button
            onClick={() => onReschedule(recipient)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-xs transition-colors"
            title="Reschedule delivery"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="hidden sm:inline">Reschedule</span>
          </button>
        )}

        {/* Cancel button - only for pending (not yet sent) */}
        {isPending && (
          <button
            onClick={() => onCancel(recipient)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 text-xs transition-colors"
            title="Cancel this recipient"
          >
            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
            <span className="hidden sm:inline">Cancel</span>
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Pagination Component ──────────────────────────────────
function Pagination({ page, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-center gap-4 py-4 border-t border-white/[0.04]">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="text-sm text-gray-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        ← Prev
      </button>
      <span className="text-gray-500 text-sm">
        Page {page} of {totalPages}
      </span>
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="text-sm text-gray-400 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        Next →
      </button>
    </div>
  );
}

// ─── Main Panel Component ──────────────────────────────────
export default function ViewRecipientsPanel({ moonbeamId, uuid, onClose }) {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  // Reschedule modal state
  const [rescheduleModal, setRescheduleModal] = useState({
    isOpen: false,
    recipient: null,
    deliveryMode: 'scheduled',
  });

  // Cancelling state
  const [cancelling, setCancelling] = useState(null);

  const fetchRecipients = () => {
    if (!moonbeamId) return;
    setLoading(true);
    setError(null);
    
    moonbeamService
      .getRecipients(moonbeamId)
      .then((data) => {
        setRecipients(data.recipients || []);
      })
      .catch((err) => {
        setError(err.data?.error || 'Failed to load recipients');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchRecipients();
  }, [moonbeamId]);

  // Pagination calculations
  const totalRecipients = recipients.length;
  const totalPages = Math.ceil(totalRecipients / RECIPIENTS_PER_PAGE);
  const startIndex = (page - 1) * RECIPIENTS_PER_PAGE;
  const endIndex = startIndex + RECIPIENTS_PER_PAGE;
  const paginatedRecipients = recipients.slice(startIndex, endIndex);

  // Stats
  const viewedCount = recipients.filter(r => r.viewcount > 0).length;
  const sentCount = recipients.filter(r => r.sentat).length;
  const pendingCount = recipients.filter(r => !r.sentat).length;

  // ─── Reschedule handlers ─────────────────────────────────
  const handleReschedule = (recipient) => {
    const deliveryMode = recipient.deliverymode === 'birthday' ? 'birthday' : 'scheduled';
    setRescheduleModal({
      isOpen: true,
      recipient,
      deliveryMode,
    });
  };

  const handleRescheduleSave = async (data) => {
    const recipientId = rescheduleModal.recipient?.recipientid;
    if (!recipientId) return;

    await moonbeamService.rescheduleRecipient(recipientId, data);
    fetchRecipients(); // Refresh the list
  };

  // ─── Cancel handlers ─────────────────────────────────────
  const handleCancel = async (recipient) => {
    const name = [recipient.firstname, recipient.lastname].filter(Boolean).join(' ')
      || recipient.recipientname
      || recipient.email;

    if (!window.confirm(`Cancel delivery to ${name}? This will remove them from this eCard.`)) {
      return;
    }

    setCancelling(recipient.recipientid);
    try {
      await moonbeamService.cancelRecipient(recipient.recipientid);
      fetchRecipients(); // Refresh the list
    } catch (err) {
      alert(err.data?.error || 'Failed to cancel recipient');
    } finally {
      setCancelling(null);
    }
  };

  if (loading) {
    return (
      <div
        className="rounded-2xl p-8 mt-3"
        style={{
          background: 'linear-gradient(135deg, rgba(15,18,35,0.95), rgba(10,12,28,0.98))',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        <div className="flex items-center justify-center gap-3">
          <div className="w-4 h-4 rounded-full bg-amber-400/20 animate-pulse" />
          <p className="text-gray-400 text-sm animate-pulse">Loading recipients...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        className="rounded-2xl p-8 mt-3"
        style={{
          background: 'linear-gradient(135deg, rgba(15,18,35,0.95), rgba(10,12,28,0.98))',
          border: '1px solid rgba(239,68,68,0.15)',
        }}
      >
        <p className="text-red-400 text-center text-sm">{error}</p>
      </div>
    );
  }

  return (
    <>
      <div
        className="rounded-2xl overflow-hidden mt-3"
        style={{
          background: 'linear-gradient(180deg, rgba(15,18,35,0.97), rgba(8,10,24,0.99))',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: '0 8px 40px rgba(0,0,0,0.4), 0 0 0 1px rgba(255,255,255,0.03) inset',
        }}
      >
        {/* ── Header ──────────────────────────────────────────────── */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{
            background: 'linear-gradient(135deg, rgba(246,197,71,0.06), rgba(246,197,71,0.01))',
            borderBottom: '1px solid rgba(246,197,71,0.1)',
          }}
        >
          <div>
            <h3
              className="font-semibold text-sm uppercase tracking-[0.15em]"
              style={{ color: '#F6C547' }}
            >
              View as Recipient
            </h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Each person sees their personalized card
            </p>
          </div>
          <div className="flex items-center gap-4">
            <p className="text-gray-500 text-xs">
              {viewedCount}/{totalRecipients} viewed · {sentCount}/{totalRecipients} sent
              {pendingCount > 0 && ` · ${pendingCount} pending`}
            </p>
            {onClose && (
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-white text-sm transition-colors duration-200 w-7 h-7 rounded-lg flex items-center justify-center hover:bg-white/[0.06]"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* ── Recipients List ─────────────────────────────────────── */}
        <div
          style={{
            background: 'rgba(0,0,0,0.15)',
          }}
        >
          {recipients.length === 0 ? (
            <div className="py-12 text-center">
              <p className="text-gray-500 text-sm">No recipients found</p>
            </div>
          ) : (
            <>
              {/* Column headers (desktop only) */}
              <div className="hidden sm:flex items-center gap-3 py-2 px-4 border-b border-white/[0.06] bg-white/[0.02]">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <span className="w-2.5" /> {/* Spacer for dot */}
                  <p className="text-gray-500 text-[11px] font-semibold uppercase tracking-widest">Recipient</p>
                </div>
                <div className="w-40 flex justify-center">
                  <p className="text-gray-500 text-[11px] font-semibold uppercase tracking-widest">Status</p>
                </div>
                <div className="w-[280px] flex justify-end">
                  <p className="text-gray-500 text-[11px] font-semibold uppercase tracking-widest">Actions</p>
                </div>
              </div>

              {/* Recipient rows */}
              <div>
                {paginatedRecipients.map((r) => (
                  <RecipientRow
                    key={r.recipientid}
                    recipient={r}
                    moonbeamUuid={uuid}
                    onReschedule={handleReschedule}
                    onCancel={handleCancel}
                  />
                ))}
              </div>

              {/* Pagination */}
              <Pagination
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </div>

        {/* ── Footer hint ─────────────────────────────────────────── */}
        {recipients.length > 0 && (
          <div
            className="px-4 py-3 text-center"
            style={{
              background: 'rgba(246,197,71,0.03)',
              borderTop: '1px solid rgba(246,197,71,0.08)',
            }}
          >
            <p className="text-gray-500 text-xs">
              Share a link if someone didn't receive the email
            </p>
          </div>
        )}
      </div>

      {/* ── Reschedule Modal ──────────────────────────────────────── */}
      <RescheduleModal
        isOpen={rescheduleModal.isOpen}
        onClose={() => setRescheduleModal({ isOpen: false, recipient: null, deliveryMode: 'scheduled' })}
        onSave={handleRescheduleSave}
        mode="single"
        deliveryMode={rescheduleModal.deliveryMode}
        recipient={rescheduleModal.recipient}
      />
    </>
  );
}